import type { CSSProperties } from 'react'
import type { ConfigParamKey, KitchensinkConfig } from './config'
import { DEPLOY_ENVS } from './env'
import { theme } from './theme'

export type InitStatus = 'idle' | 'pending' | 'done' | 'error'

const inputStyle: CSSProperties = {
  backgroundColor: theme.surfaceElevated,
  border: `1px solid ${theme.border}`,
  borderRadius: '7px',
  color: theme.text,
  fontSize: '13px',
  padding: '8px 12px',
  outline: 'none',
  width: '200px',
  fontFamily: 'inherit',
}

const labelStyle: CSSProperties = {
  fontSize: '10px',
  color: theme.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  fontWeight: 600,
  marginBottom: '5px',
  display: 'block',
}

type Props = {
  config: KitchensinkConfig
  initStatus: InitStatus
  tenantChangedAfterInit: boolean
  onParamChange: (key: ConfigParamKey, value: string) => void
  onInit: () => void
}

export const ConfigBar = ({
  config,
  initStatus,
  tenantChangedAfterInit,
  onParamChange,
  onInit,
}: Props) => {
  const { tenantSubdomain, tenantId, env, autoInit } = config
  const canInit = Boolean(tenantSubdomain && tenantId)

  return (
    <div
      style={{
        backgroundColor: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        padding: '16px 28px',
        display: 'flex',
        alignItems: 'flex-end',
        gap: '16px',
        flexWrap: 'wrap',
      }}
    >
      <div>
        <label htmlFor='ks-tenant-subdomain' style={labelStyle}>
          Tenant Subdomain
        </label>
        <input
          id='ks-tenant-subdomain'
          value={tenantSubdomain}
          onChange={e => onParamChange('tenantSubdomain', e.target.value)}
          placeholder='e.g. acme'
          style={inputStyle}
        />
      </div>

      <div>
        <label htmlFor='ks-tenant-id' style={labelStyle}>
          Tenant ID
        </label>
        <input
          id='ks-tenant-id'
          value={tenantId}
          onChange={e => onParamChange('tenantId', e.target.value)}
          placeholder='e.g. abc-123-def'
          style={inputStyle}
        />
      </div>

      <div>
        <label htmlFor='ks-env' style={labelStyle}>
          Environment
        </label>
        <select
          id='ks-env'
          data-test-id='env-select'
          value={env}
          onChange={e => onParamChange('env', e.target.value)}
          style={{ ...inputStyle, width: '140px', height: '35px' }}
        >
          {DEPLOY_ENVS.map(deployEnv => (
            <option key={deployEnv} value={deployEnv}>
              {deployEnv}
            </option>
          ))}
        </select>
      </div>

      <label
        htmlFor='ks-autoinit'
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '12px',
          color: theme.textMuted,
          height: '35px',
          cursor: 'pointer',
        }}
      >
        <input
          id='ks-autoinit'
          data-test-id='autoinit-checkbox'
          type='checkbox'
          checked={autoInit}
          onChange={e => onParamChange('autoinit', e.target.checked ? '1' : '')}
          style={{ accentColor: theme.accent }}
        />
        Auto-init on page load
      </label>

      <button
        data-test-id='init-button'
        onClick={onInit}
        disabled={!canInit || initStatus === 'pending' || initStatus === 'done'}
        style={{
          backgroundColor: canInit && initStatus !== 'done' ? theme.accent : theme.surfaceElevated,
          color: canInit && initStatus !== 'done' ? '#fff' : theme.textDim,
          border: `1px solid ${canInit && initStatus !== 'done' ? theme.accent : theme.border}`,
          borderRadius: '7px',
          padding: '8px 18px',
          fontSize: '13px',
          fontWeight: 600,
          cursor:
            canInit && initStatus !== 'pending' && initStatus !== 'done'
              ? 'pointer'
              : 'not-allowed',
          fontFamily: 'inherit',
          transition: 'all 0.15s',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          height: '35px',
        }}
      >
        <span>{initStatus === 'pending' ? '⏳' : '🔥'}</span>
        {initStatus === 'pending' ? 'Initializing…' : 'Initialize'}
      </button>

      {initStatus === 'done' && (
        <div
          data-test-id='init-status-done'
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12px',
            color: theme.success,
            backgroundColor: theme.successSubtle,
            padding: '6px 12px',
            borderRadius: '6px',
            border: `1px solid rgba(34, 197, 94, 0.2)`,
          }}
        >
          <span>✓</span> Snippet initialized
        </div>
      )}

      {initStatus === 'error' && (
        <div
          data-test-id='init-status-error'
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12px',
            color: '#f87171',
            backgroundColor: 'rgba(248, 113, 113, 0.1)',
            padding: '6px 12px',
            borderRadius: '6px',
            border: `1px solid rgba(248, 113, 113, 0.2)`,
          }}
        >
          <span>✕</span> Initialization failed — check the console
        </div>
      )}

      {tenantChangedAfterInit && (
        <div
          data-test-id='init-status-stale'
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12px',
            color: '#f59e0b',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            padding: '6px 12px',
            borderRadius: '6px',
            border: `1px solid rgba(245, 158, 11, 0.2)`,
          }}
        >
          <span>⚠</span> Tenant changed — reload the page to re-initialize with the new
          configuration
        </div>
      )}
    </div>
  )
}
